import { diffChars } from 'diff';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KeyboardArrowDown } from '@mui/icons-material';
import { Collapse, Typography } from '@mui/material';

import MarkdownElement from 'main/components/utils/MarkdownElement';
import useHistoryOfChangesContext from 'pages/item/v2/hooks/use-history-of-changes-context';
import Html from 'utils/Html';

import useStyles from './styles';

interface Props {
    description: string;
    privateArea: number;
}

const Description: React.FC<Props> = ({ description, privateArea }) => {
    const { classes, cx } = useStyles();
    const containerRef = useRef<HTMLDivElement>(null);

    const [open, setOpen] = useState(false);
    const [collapseSize, setCollapseSize] = useState(0);
    const { revision } = useHistoryOfChangesContext();

    /** replace for markdown wrong parsed Bold text */
    const refineMd = useCallback((md: string) => md.replace(/\*\*\s?(.+?)\s\*\*/g, '**$1**'), []);

    const content = useMemo(() => {
        const newValue = refineMd(description ?? '');

        if (revision && 'description' in revision.changes) {
            const oldValue = refineMd(revision.changes.description ?? '');

            if (oldValue === newValue) {
                return newValue ? <MarkdownElement className={classes.body} text={newValue} /> : null;
            }

            const html = diffChars(oldValue, newValue, { ignoreCase: true }).reduce(
                (acc, { value, added, removed }) => {
                    if (added) return `${acc}<span class=${classes.added}>${value}</span>`;
                    if (removed) return `${acc}<span class=${classes.removed}>${value}</span>`;
                    return `${acc}${value}`;
                },
                ''
            );

            return html ? <Html className={classes.body} html={html} /> : null;
        }

        return newValue ? <MarkdownElement className={classes.body} text={newValue} /> : null;
    }, [classes.added, classes.body, classes.removed, description, refineMd, revision]);

    useEffect(() => {
        if (containerRef.current && containerRef.current.clientHeight > window.innerHeight) {
            setCollapseSize(window.innerHeight);
        }
    }, []);

    if (!content) {
        return null;
    }

    return (
        <div className={classes.description} ref={containerRef}>
            {collapseSize ? (
                <React.Fragment>
                    <Collapse
                        in={open}
                        collapsedSize={collapseSize}
                        classes={{ root: cx({ [classes.mask]: !!privateArea || !open }) }}
                    >
                        {content}
                    </Collapse>
                    <div className={classes.more} onClick={() => setOpen(!open)}>
                        <Typography variant="body2">{open ? 'Less' : 'More'}</Typography>
                        <KeyboardArrowDown
                            className={classes.icon}
                            style={{ transform: open ? 'rotate(-180deg)' : 'initial' }}
                        />
                    </div>
                </React.Fragment>
            ) : (
                content
            )}
        </div>
    );
};

export default Description;
